import { action, computed } from '@ember/object';
import { not } from '@ember/object/computed';
import { task, dropTask } from 'ember-concurrency';
import { inject as service } from '@ember/service';
import ConversionsController from 'cio/controllers/conversions';
import AnalyticsService from 'cio/services/analytics';
import AuthService from 'cio/services/auth';
import EnvironmentService from 'cio/services/environment';
import CampaignModel, { CampaignRestartModes, CampaignUpdateTypes } from 'cio/models/campaign';
import { ConversionType, showConversionEventErrors, hasEventAttributeComparison } from 'cio/utils/conversions';
import { SerializedLegacySegmentNegatableFilter } from 'cio/types/conditions/legacy-filter';
import { tracked } from '@glimmer/tracking';
import { Filters } from 'cio/utils/filters';
import DS from 'ember-data';
import ShortSegmentModel from 'cio/models/short-segment';

export default class IndexController extends ConversionsController {
  @service analytics!: AnalyticsService;
  @service auth!: AuthService;
  @service environment!: EnvironmentService;
  @service store!: DS.Store;

  declare model: CampaignModel;

  isCampaignOverview = false;
  conversionTracking = false;
  @tracked hasAcceptedEventTimeStorageLimit = false;
  @tracked shortSegment: ShortSegmentModel | null = null;

  readonly faqs: { question: string; answer: string }[] = [
    {
      question: 'When does a campaign evaluate exit criteria?',
      answer:
        'The system checks who meets exit criteria <strong>before sending every message</strong> in a campaign. Whoever meets it, exits.',
    },
    {
      question: 'What gets marked as converted? People, journeys, or deliveries?',
      answer: 'Only deliveries are marked as converted.',
    },
    {
      question: 'Why is the last delivery sent not marked as converted?',
      answer:
        "Let's say the goal is for people to perform event X, and it counts as a conversion when it happens within 2 weeks after they click a link in any delivery." +
        '<br><br>Imagine that a person receives three deliveries but clicks a link in the first one and performs event X within the two week time frame. In this case, <strong>the first delivery will' +
        ' be marked as converted, but not the second or third</strong>.',
    },
  ];

  private createTriggerFilter(segmentId: string, negate: boolean): SerializedLegacySegmentNegatableFilter {
    return {
      type: 'segments_negatable',
      segments: [parseInt(segmentId)],
      negate,
    };
  }

  private hasSegmentInTriggerFilters() {
    let { conversionType, filters } = this.model;

    if (this.model.conversionSegmentId == null) {
      return false;
    }
    let conversionSegmentId = parseInt(this.model.conversionSegmentId);

    let exitSegment = conversionType === ConversionType.ExitSegment;

    return filters.some((filter) => {
      return filter.segments.some((id) => id === conversionSegmentId) && filter.negate !== exitSegment;
    });
  }

  @computed('hasAcceptedEventTimeStorageLimit', 'eventConversionCondition.{isDirty,children.@each.comparisonOperator}')
  get showEventAttributeDataBanner() {
    let isComparingToEventData = hasEventAttributeComparison(this.eventConversionCondition);
    return (
      !this.hasAcceptedEventTimeStorageLimit &&
      Boolean(this.eventConversionCondition?.isDirty) &&
      isComparingToEventData
    );
  }

  @computed('model.{exitOnTriggerOrFilterNotMatched,exitOnConversionMatched}')
  get willExit() {
    return this.model.exitOnTriggerOrFilterNotMatched || this.model.exitOnConversionMatched;
  }

  @not('willExit') neverExit!: boolean;

  @computed('model.{anchors,filters}')
  get exitOnTriggerOrFilterLabel() {
    let { anchors, filters } = this.model;
    let hasAnchors = anchors?.length > 0;
    let hasFilters = filters?.length > 0;
    let label = "They don't match the campaign's";

    if (hasAnchors && hasFilters) {
      label = `${label} triggers and filters:`;
    } else if (hasAnchors) {
      label = `${label} triggers:`;
    } else if (hasFilters) {
      label = `${label} filters:`;
    } else {
      label = `${label} triggers and filters`;
    }

    return label;
  }

  @computed('model.validConversion', 'eventConversionCondition.hasErrors')
  get isInvalid() {
    let eventConditionHasErrors = this.eventConversionCondition?.hasErrors ?? false;
    return showConversionEventErrors(this.model, eventConditionHasErrors);
  }

  @computed(
    'model.{conversion,conversionType,conversionSegmentId,conversionEventName,validConversionTimeframe}',
    'eventConversionCondition.hasErrors',
  )
  get invalidErrorMessage() {
    if (typeof this.model.conversion !== 'boolean') {
      return 'Please set up conversion goal to save your campaign!';
    }

    if (!this.model.conversion) {
      // skipped conversion goal:
      return;
    }

    if (
      (this.model.conversionType === ConversionType.EnterSegment ||
        this.model.conversionType === ConversionType.ExitSegment) &&
      !this.model.conversionSegmentId
    ) {
      return 'Please choose valid conversion segment!';
    }

    if (this.model.conversionType === ConversionType.PerformEvent && !this.model.conversionEventName?.trim()) {
      return 'Please enter conversion event name!';
    }

    if (this.eventConversionCondition?.hasErrors ?? false) {
      return 'Please double check the event filters!';
    }

    if (!this.model.validConversionTimeframe) {
      return 'Please select a valid time frame!';
    }

    return;
  }

  @computed('model.hasDirtyAttributes', 'eventConversionCondition.isDirty')
  get isDirty() {
    let { model, eventConversionCondition } = this;
    return model.hasDirtyAttributes || eventConversionCondition?.isDirty;
  }

  @computed('model.conversionAction')
  get campaignConversionActionInPastTense(): string {
    let { conversionAction } = this.model;

    // eslint-disable-next-line spellcheck/spell-checker
    if (conversionAction.slice(-3) === 'ing') {
      return `${conversionAction.slice(0, -3)}ed`;
    }

    return conversionAction;
  }

  get conversionSegmentName() {
    return this.shortSegment?.name ?? '';
  }

  @computed(
    'model.{conversionSegmentId,isSegmentConversion,isApiTriggered,conversionType,filters,isSegmentTriggered,restartMode}',
  )
  get showSegmentBanner() {
    let { conversionSegmentId, isSegmentConversion, isApiTriggered, isSegmentTriggered, restartMode } = this.model;

    // Prevent Segment campaign: filters + frequency. It is disabled.
    if (isSegmentTriggered && restartMode !== CampaignRestartModes.None) {
      return false;
    }

    return isSegmentConversion && conversionSegmentId != null && !isApiTriggered && !this.hasSegmentInTriggerFilters();
  }

  get hasReadOnlyPermission() {
    if (this.model.isApiTriggered) {
      return !this.auth.hasPermission('journeys/broadcasts/edit');
    }

    return !this.auth.hasPermission('journeys/campaigns/edit');
  }

  get disabled() {
    return this.model.isLocked || this.hasReadOnlyPermission;
  }

  saveCampaignTask = dropTask(async () => {
    let { model } = this;

    model.set('updateType', CampaignUpdateTypes.ExitAndConversion);

    if (model.conversionEventName != null) {
      model.set('conversionEventName', model.conversionEventName.trim());
    }

    let res = await model.save();
    this.resetConversionSnapshot();

    // This is a hack to reset the `isDirty` state on the group condition and its children
    this.createGroupCondition(this.model.conversionEventFilters);

    return res;
  });

  resetTask = task(async () => {
    await this.model.rollbackAttributes();
    this.createGroupCondition(this.model.conversionEventFilters);
    this.analytics.track('campaign', 'edit_discarded', { model: this.model, from: 'goal_and_exit' });
  });

  @action
  async onAddTriggerFilter() {
    let { conversionType, conversionSegmentId } = this.model;
    let negate = conversionType === ConversionType.EnterSegment;
    let newFilter = this.createTriggerFilter(conversionSegmentId as string, negate);
    this.model.set('filters', [...this.model.filters, newFilter]);

    // Save goal and exit settings
    await this.saveCampaignTask.perform();
  }

  @action
  neverExitSelected() {
    this.model.setProperties({
      exitOnTriggerOrFilterNotMatched: false,
      exitOnConversionMatched: false,
    });
  }

  @action
  didUpdate() {
    this.fetchShortSegment();
  }

  protected fetchShortSegment() {
    if (this.model.conversionSegmentId == null) {
      this.shortSegment = null;
      return;
    }
    this.shortSegment = this.store.peekRecord('short-segment', this.model.conversionSegmentId);
    return;
  }

  @action
  exitSelected() {
    this.model.set('exitOnTriggerOrFilterNotMatched', true);
  }

  @action
  toggleConversionTracking() {
    this.set('conversionTracking', !this.conversionTracking);
  }

  @action
  onDisableConversion() {
    super.onDisableConversion();
    // If they had an exit condition selected that used the conversion, we need
    // to disable that
    this.model.set('exitOnConversionMatched', false);
  }

  @action
  didInsert() {
    this.fetchShortSegment();
  }

  @action
  showEventStorageDataLimitBanner() {
    if (!hasEventAttributeComparison(this.eventConversionCondition)) {
      this.hasAcceptedEventTimeStorageLimit = false;
    }
  }

  @action
  updateEventConversionFilters(serializedFilters: Filters | null) {
    this.showEventStorageDataLimitBanner();
    this.model.set('conversionEventFilters', serializedFilters);
  }
}
